import React from 'react';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useMemo, useRef } from 'react';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { DashboardProductionDetailDto, DashboardKpiSendDetailDto } from '../../Dto_Home';

const PRODUCTION_COLUMN_STATE_KEY = 'home-a-production-detail-column-state';
const KPI_COLUMN_STATE_KEY = 'home-a-kpi-send-detail-column-state';
const ignoreDashboardGridState = () => undefined;

interface TablesSectionProps {
  productionDetailData: DashboardProductionDetailDto[];
  kpiSendDetailData: DashboardKpiSendDetailDto[];
}

//상세 생산실적 테이블 컬럼 정의
const productionDetailColumns: ColDef[] = [
  ...getDefaultRowIndexColumnConfig((params: any) => {
    if (params.node.rowPinned === 'bottom') return '';
    return params.node.rowIndex + 1;
  }),
  {
    field: 'groupKey',
    headerName: '생산일자',
    width: 150,
    valueFormatter: (params: any) => params.value || '-'
  },
  {
    field: 'itemNm',
    headerName: '품목명',
    width: 120,
    valueFormatter: (params: any) => params.node?.rowPinned === 'bottom' ? '' : (params.value || '-')
  },
  {
    field: 'itemSpec',
    headerName: '규격',
    width: 120,
    valueFormatter: (params: any) => params.node?.rowPinned === 'bottom' ? '' : (params.value || '-')
  },
  {
    field: 'modelNo',
    headerName: '모델번호',
    width: 120,
    valueFormatter: (params: any) => params.node?.rowPinned === 'bottom' ? '' : (params.value || '-')
  },
  {
    field: 'productQty',
    headerName: '생산량',
    width: 100,
    valueFormatter: (params: any) => {
      if (params.node?.rowPinned === 'bottom' && (params.value == null || params.value === '')) return '';
      const value = params.value;
      return typeof value === 'number' ? value.toLocaleString() : (value || '-');
    },
    cellStyle: { textAlign: 'right' }
  },
  {
    field: 'badQty',
    headerName: '불량',
    width: 100,
    valueFormatter: (params: any) => {
      if (params.node?.rowPinned === 'bottom' && (params.value == null || params.value === '')) return '';
      const value = params.value;
      return typeof value === 'number' ? value.toLocaleString() : (value || '-');
    },
    cellStyle: { color: 'red', textAlign: 'right' }
  },
  {
    field: 'acceptQty',
    headerName: '입고량',
    width: 100,
    valueFormatter: (params: any) => {
      if (params.node?.rowPinned === 'bottom' && (params.value == null || params.value === '')) return '';
      const value = params.value;
      return typeof value === 'number' ? value.toLocaleString() : (value || '-');
    },
    cellStyle: { textAlign: 'right' }
  }
];

//KPI모니터링 테이블 컬럼 정의
const kpiSendDetailColumns: ColDef[] = [
  ...getDefaultRowIndexColumnConfig((params: any) => {
    if (params.node.rowPinned === 'bottom') return '';
    return params.node.rowIndex + 1;
  }),
  {
    field: 'transDate',
    headerName: '전송일자',
    width: 120,
    valueFormatter: (params: any) => params.value || '-'
  },
  {
    field: 'kpiName',
    headerName: 'KPI명',
    width: 100,
    valueFormatter: (params: any) => params.node?.rowPinned === 'bottom' ? '' : (params.value || '-')
  },
  {
    field: 'msmtvl',
    headerName: '실적량',
    width: 100,
    cellStyle: { textAlign: 'right' },
    valueFormatter: (params: any) => {
      if (params.node?.rowPinned === 'bottom' && (params.value == null || params.value === '')) return '';
      const value = params.value;
      return typeof value === 'number' ? value.toLocaleString() : (value || '-');
    }
  },
  {
    field: 'unit',
    headerName: '단위',
    width: 80,
    valueFormatter: (params: any) => params.node?.rowPinned === 'bottom' ? '' : (params.value || '-')
  },
  {
    field: 'achrt',
    headerName: '성장량',
    width: 100,
    valueFormatter: (params: any) => {
      if (params.node?.rowPinned === 'bottom' && (params.value == null || params.value === '')) return '';
      const value = params.value;
      return typeof value === 'number' ? value.toLocaleString() : (value || '-');
    },
    cellStyle: { textAlign: 'right' }
  },
  {
    field: 'unt',
    headerName: '단위',
    width: 80,
    valueFormatter: (params: any) => params.node?.rowPinned === 'bottom' ? '' : (params.value || '%')
  }
];

const defaultTableConfig: ColDef = {
  resizable: true,
  sortable: true,
};

export const TablesSection: React.FC<TablesSectionProps> = ({
  productionDetailData,
  kpiSendDetailData
}) => {
  const productionGridRef = useRef<AgGridReact<DashboardProductionDetailDto> | null>(null);
  const kpiGridRef = useRef<AgGridReact<DashboardKpiSendDetailDto> | null>(null);

  const {
    paddedGridData: paddedProductionData,
    getRowId: getProductionRowId,
    createCellClassRules: createProductionCellClassRules,
  } = useGridSettingContent<any>({
    gridRef: productionGridRef as React.RefObject<AgGridReact<any> | null>,
    key: 'rowIndex',
    gridData: productionDetailData,
    defaultGridData: {} as any,
    selectedRow: undefined,
  });

  const {
    paddedGridData: paddedKpiData,
    getRowId: getKpiRowId,
    createCellClassRules: createKpiCellClassRules,
  } = useGridSettingContent<any>({
    gridRef: kpiGridRef as React.RefObject<AgGridReact<any> | null>,
    key: 'rowIndex',
    gridData: kpiSendDetailData,
    defaultGridData: {} as any,
    selectedRow: undefined,
  });

  const productionDefaultColDef = useMemo<ColDef>(() => ({
    ...defaultTableConfig,
    cellClassRules: createProductionCellClassRules({
      idKey: 'rowIndex',
      selectedList: [],
      deletedList: [],
      disabledInsertedColumns: [],
      disabledColumns: [],
      pointerInsertedColumns: [],
      pointerColumns: [],
    }),
  }), [createProductionCellClassRules]);

  const kpiDefaultColDef = useMemo<ColDef>(() => ({
    ...defaultTableConfig,
    cellClassRules: createKpiCellClassRules({
      idKey: 'rowIndex',
      selectedList: [],
      deletedList: [],
      disabledInsertedColumns: [],
      disabledColumns: [],
      pointerInsertedColumns: [],
      pointerColumns: [],
    }),
  }), [createKpiCellClassRules]);

  const productionPinnedBottomRow = useMemo(
    () => [{ 
      rowIndex: '', 
      groupKey: `${productionDetailData.length.toLocaleString()}건`,
      productQty: productionDetailData.reduce(
        (sum, item) => sum + (Number(item.productQty) || 0),
        0
      )
    }],
    [productionDetailData]
  );
  const kpiPinnedBottomRow = useMemo(
    () => [{
      rowIndex: '',
      transDate: `${kpiSendDetailData.length.toLocaleString()}건`,
      msmtvl: kpiSendDetailData.reduce(
        (sum, item) => sum + (Number(item.msmtvl) || 0),
        0
      ),
      achrt: kpiSendDetailData.reduce(
        (sum, item) => sum + (Number(item.achrt) || 0),
        0
      )
    }],
    [kpiSendDetailData]
  );

  return (
    <div className="grid grid-cols-2 gap-4" style={{ height: '40%' }}>
      {/* 상세 생산실적 테이블 */}
      <div className="bg-white p-4 rounded-lg shadow flex flex-col">
        <h2 className="text-lg font-semibold mb-2">상세 생산실적</h2>
        <div className="ag-theme-alpine flex-1 min-h-0">
          <GridContextWrapper<DashboardProductionDetailDto>
            gridRef={productionGridRef}
            paddedGridData={paddedProductionData}
            baseColumnDefs={productionDetailColumns}
            setColumnState={ignoreDashboardGridState}
            localStorageKey={PRODUCTION_COLUMN_STATE_KEY}
            enableCellCalculation
            enableDataHistory={false}
          >
            <Grid_Touch<DashboardProductionDetailDto>
              ref={productionGridRef}
              columnDefs={productionDetailColumns}
              rowData={paddedProductionData}
              getRowId={getProductionRowId}
              pinnedBottomRowData={productionPinnedBottomRow}
              defaultColDef={productionDefaultColDef}
              setHoveredRowIndex={ignoreDashboardGridState}
              columnStateKey={PRODUCTION_COLUMN_STATE_KEY}
              setColumnState={ignoreDashboardGridState}
            />
          </GridContextWrapper>
        </div>
      </div>

      {/* KPI 전송내역 테이블 */}
      <div className="bg-white rounded-lg p-3 shadow-sm flex flex-col">
      <h2 className="text-lg font-semibold mb-2">KPI전송내역</h2>
        <div className="ag-theme-alpine flex-1 min-h-0">
          <GridContextWrapper<DashboardKpiSendDetailDto>
            gridRef={kpiGridRef}
            paddedGridData={paddedKpiData}
            baseColumnDefs={kpiSendDetailColumns}
            setColumnState={ignoreDashboardGridState}
            localStorageKey={KPI_COLUMN_STATE_KEY}
            enableCellCalculation
            enableDataHistory={false}
          >
            <Grid_Touch<DashboardKpiSendDetailDto>
              ref={kpiGridRef}
              columnDefs={kpiSendDetailColumns}
              rowData={paddedKpiData}
              getRowId={getKpiRowId}
              pinnedBottomRowData={kpiPinnedBottomRow}
              defaultColDef={kpiDefaultColDef}
              setHoveredRowIndex={ignoreDashboardGridState}
              columnStateKey={KPI_COLUMN_STATE_KEY}
              setColumnState={ignoreDashboardGridState}
            />
          </GridContextWrapper>
        </div>
      </div>
    </div>
  );
};
